import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { combineLatest, Observable } from 'rxjs';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { map, startWith, take, tap } from 'rxjs/operators';
import { searchObjectKeys } from '../shared/data-source-filter-predicate';
import { difference } from 'lodash-es';
import { LocalScanningWebApiService } from '../../services/local-scanning-web-api.service';
import { Router } from '@angular/router';
import { AlertsTableComponent } from './table/table.component';
import { PendingCollectionService } from '../../services/pending-collection.service';
import { AlertType, Pending, PendingWithAlerts } from '../biographics/pending.model';
import { alertTypeToFriendly } from './alert-type.pipe';
import { EcaMonotronModule, EcaNoResultsModule, EcaSearchModule } from '@drc-eca/eca-components-lib';
import { NavComponent } from '../shared/nav/nav.component';
import { MatFormField } from '@angular/material/input';
import { MatButtonToggle, MatButtonToggleGroup } from '@angular/material/button-toggle';
import { AsyncPipe } from '@angular/common';
import { MatButton } from '@angular/material/button';
import { ThrottleClickDirective } from '@drc-eca/eca-lrc-common-ui';


@Component({
  selector: 'eca-alerts',
  templateUrl: './alerts.component.html',
  styleUrls: ['./alerts.component.scss'],
  imports: [EcaMonotronModule, NavComponent, FormsModule, ReactiveFormsModule, MatFormField, EcaSearchModule, MatButtonToggleGroup, MatButtonToggle, MatButton, ThrottleClickDirective, AlertsTableComponent, EcaNoResultsModule, AsyncPipe]
})
export class AlertsComponent implements OnInit {
  builder = inject(UntypedFormBuilder);
  api = inject(LocalScanningWebApiService);
  router = inject(Router);
  collectionService = inject(PendingCollectionService);


  @ViewChild('alertsTable') table: AlertsTableComponent;

  form: UntypedFormGroup;
  pending: PendingWithAlerts[] = [];
  selected: PendingWithAlerts[] = [];

  filteredPending$: Observable<PendingWithAlerts[]>;

  hasBlankAlerts$: Observable<boolean>;
  hasMultiAlerts$: Observable<boolean>;
  hasScoreInputAlerts$: Observable<boolean>;
  hasBiographicAlerts$: Observable<boolean>;
  hasSubmitAlerts$: Observable<boolean>;

  alertCount$: Observable<number>;

  readonly filterKeys: Array<keyof Pending> = [
    'lithocode',
    'form',
    'stateStudentId',
    'firstName',
    'lastName',
    'alerts',
  ];

  constructor() {
    this.form = this.builder.group({
      search: '',
      alertTypes: [['blank', 'multi', 'scoreInput', 'biographic', 'submit']],
    });
  }

  clearSelectedAlerts() {
    if (!this.selected.length) {
      return;
    }

    this.api.clearAlerts(this.selected)
      .pipe(take(1))
      .subscribe(
        () => {
          this.onClearAlertsSuccess();
        },
        (/* failed */) => {
          this.onClearAlertsFailed();
        }
      );
  }

  onClearAlertsSuccess() {
    if (this.table) {
      const remaining = difference(this.pending, this.selected);
      this.collectionService.clearAlertsForEach(this.selected);
      this.table.clearSelection(this.selected);
      this.selected = [];
      if (!remaining.length) {
        this.router.navigateByUrl('/biographics');
      }
    }
  }

  /* Clear selection and sync up pending collection as there may have been out of sync docs */
  onClearAlertsFailed() {
    if (this.table) {
      this.collectionService.syncPending();
      this.table.clearSelection();
    }
  }

  onSelection(selected: PendingWithAlerts[]) {
    this.selected = selected;
  }

  hasAlertType(alertType: AlertType): Observable<boolean> {
    return this.collectionService.alerts$.pipe(
      map(pendings => !!pendings.find(pending => pending.hasAlertOfType(alertType))),
    );
  }

  initAlertToggles() {
    this.hasBlankAlerts$ = this.hasAlertType('blank');
    this.hasMultiAlerts$ = this.hasAlertType('multi');
    this.hasScoreInputAlerts$ = this.hasAlertType('scoreInput');
    this.hasBiographicAlerts$ = this.hasAlertType('biographic');
    this.hasSubmitAlerts$ = this.hasAlertType('submit');
  }

  ngOnInit() {

    const pendingWithAlerts$ = this.collectionService.alerts$.pipe(
      tap(alerts => {
        this.pending = alerts;
      })
    );

    this.initAlertToggles();

    this.filteredPending$ = combineLatest([
      pendingWithAlerts$,
      this.form.get('search').valueChanges.pipe(startWith('')),
      this.form.get('alertTypes').valueChanges.pipe(startWith(['blank', 'multi', 'scoreInput', 'biographic', 'submit'])),
    ]).pipe(
      map(([pendings, search, alertTypes]) => {
        return pendings
          .filter(pending => this.hasInFilteredSearch(pending, search))
          .filter(item => {
            return item.alerts.filter(alertItem => alertTypes.includes(alertItem.alertType)).length;
          });
      }),
    );

    this.alertCount$ = this.collectionService.alertCount$;
  }

  /* special search with limited alerts options */
  hasInFilteredSearch(pending: PendingWithAlerts, searchTerm): boolean {
    const item: any = {...pending};
    item.alerts = item.alerts.map(alerts => alertTypeToFriendly[alerts.alertType]);
    return searchObjectKeys(item, this.filterKeys, searchTerm);
  }

}
